import {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  DEFAULT_THEME,
  STORAGE_KEY,
  isTheme,
  THEMES,
  type ThemeName,
} from "./themes";

export interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Optional storage-backed value. Defaults to reading `localStorage` and
   * falling back to the OS color-scheme preference.
   */
  defaultTheme?: ThemeName;
  /**
   * `data-theme` attribute (default) or `.theme-<name>` class (attribute=false).
   */
  attribute?: "data-theme" | "class";
  /** Control density — drives `--control-h-*`, `--inset-*`, `--stack-gap-*`. */
  defaultDensity?: Density;
}

export interface ThemeContextValue {
  theme: ThemeName;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: ThemeName) => void;
  toggleTheme: () => void;
  availableThemes: ThemeName[];
  density: Density;
  setDensity: (density: Density) => void;
}

export type Density = "comfortable" | "compact";

const DENSITY_STORAGE_KEY = "ito.density";

const applyDensity = (density: Density) => {
  document.documentElement.setAttribute("data-density", density);
};

const isDensity = (value: string | null): value is Density =>
  value === "comfortable" || value === "compact";

const ThemeContext = createContext<ThemeContextValue | null>(null);

const applyTheme = (theme: ThemeName, attribute: "data-theme" | "class") => {
  const root = document.documentElement;
  if (attribute === "class") {
    root.classList.remove(
      ...(Array.from(root.classList) ?? []).filter((c) =>
        c.startsWith("theme-"),
      ),
    );
    root.classList.add(`theme-${theme}`);
  } else {
    root.setAttribute("data-theme", theme);
  }
};

/**
 * Wraps the app and controls which design-system theme is active.
 *
 * The theme is applied to the document root, which drives every token in
 * `styles/tokens.css` — so switching themes here restyles the entire product
 * with zero application changes.
 */
export function ThemeProvider({
  children,
  defaultTheme = DEFAULT_THEME,
  attribute = "data-theme",
  defaultDensity = "comfortable",
}: ThemeProviderProps) {
  const systemTheme = (): ThemeName | null => {
    if (typeof window === "undefined" || !window.matchMedia) return null;
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : null;
  };

  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (typeof window === "undefined") return defaultTheme;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) return stored;
    return systemTheme() ?? defaultTheme;
  });

  const [density, setDensityState] = useState<Density>(() => {
    if (typeof window === "undefined") return defaultDensity;
    const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return isDensity(stored) ? stored : defaultDensity;
  });

  // useLayoutEffect so the theme attribute lands before first paint — no
  // flash of the default theme on load.
  useLayoutEffect(() => {
    applyTheme(theme, attribute);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage unavailable — ignore */
    }
  }, [theme, attribute]);

  useLayoutEffect(() => {
    applyDensity(density);
    try {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
    } catch {
      /* storage unavailable — ignore */
    }
  }, [density]);

  const setTheme = useCallback((next: ThemeName) => setThemeState(next), []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const index = THEMES.findIndex((t) => t.id === prev);
      return THEMES[(index + 1) % THEMES.length]!.id;
    });
  }, []);

  const setDensity = useCallback((next: Density) => setDensityState(next), []);

  const value = useMemo<ThemeContextValue>(() => {
    // Resolve via each theme's declared appearance (dark themes count as
    // dark even when their id isn't literally "dark").
    const resolved: "light" | "dark" =
      THEMES.find((t) => t.id === theme)?.appearance ?? "light";
    return {
      theme,
      resolvedTheme: resolved,
      setTheme,
      toggleTheme,
      availableThemes: THEMES.map((t) => t.id),
      density,
      setDensity,
    };
  }, [theme, setTheme, toggleTheme, density, setDensity]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export { ThemeContext };
